import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Snackbar,
} from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

function NewUserRequests() {
  const navigate = useNavigate();

  const selectedManufacturer = JSON.parse(localStorage.getItem("selectedMfr"));
  const store_id = JSON.parse(localStorage.getItem("store_id"));
  const mfr_id = selectedManufacturer?.id;

  const [pendingNonExistingUsers, setPendingNonExistingUsers] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  // Fetch Users
  useEffect(() => {
    const fetchNonExistingUsers = async () => {
      try {
        const url = `${API_BASE_URL}/api/auth/non-existing-users/${store_id}/${mfr_id}`;
        const { data } = await axios.get(url);

        if (data.statusCode === 200 && data?.data?.users) {
          setPendingNonExistingUsers(data.data.users);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchNonExistingUsers();
  }, []);

  // Date Converter
  const dateConverter = (dateString) => {
    if (!dateString) return "";
    let date = new Date(dateString);
    if (isNaN(date)) {
      const parts = dateString.split(" ");
      const [y, m, d] = parts[0].split("-");
      const [time, meridian] = parts[1] ? parts[1].split(" ") : ["00:00", "AM"];
      let [hours, minutes] = time.split(":").map(Number);
      if (meridian?.toLowerCase() === "pm" && hours < 12) hours += 12;
      if (meridian?.toLowerCase() === "am" && hours === 12) hours = 0;
      date = new Date(y, m - 1, d, hours, minutes);
    }
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day}-${month}-${year} at ${hours}:${minutes}`;
  };

  // Filtered Users
  const filteredUsers = useMemo(() => {
    const filtered = pendingNonExistingUsers.filter((u) =>
      showHistory ? u.is_created === true : u.is_created === false,
    );

    return filtered.sort((a, b) => {
      const dateA = new Date(showHistory ? a.created_at : a.login_time);
      const dateB = new Date(showHistory ? b.created_at : b.login_time);

      return dateB - dateA; // newest first
    });
  }, [pendingNonExistingUsers, showHistory]);

  return (
    <Box
      sx={{
        bgcolor: "#394A5C",
        minHeight: "100vh",
        width: "100%",
        p: { xs: 2, sm: 6 },
      }}
    >
      {/* NAVIGATE BACK & HOME BUTTONS */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1,
          mb: 2,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 3,
            bgcolor: "#FFF",
            p: 0.5,
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              bgcolor: "#e0e0e0",
              "& svg": {
                fill: "#056b05ff",
              },
            },
          }}
        >
          <ArrowBackIosNewRoundedIcon
            onClick={() => navigate(-1)}
            sx={{
              fontSize: 20,
              fill: "#42A996",
              transition: "all 0.2s ease",
            }}
          />
        </Box>

        <Box
          sx={{
            width: 36,
            height: 36,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 3,
            bgcolor: "#FFF",
            p: 0.5,
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              bgcolor: "#e0e0e0",
              "& svg": {
                fill: "#056b05ff",
              },
            },
          }}
        >
          <HomeRoundedIcon
            onClick={() => navigate("/admin-dash")}
            sx={{
              fontSize: 20,
              fill: "#42A996",
              transition: "all 0.2s ease",
            }}
          />
        </Box>
      </Box>

      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Typography sx={{ color: "#fff", fontSize: 24, fontWeight: 600 }}>
          New User Requests
        </Typography>

        <Button
          onClick={() => setShowHistory((prev) => !prev)}
          sx={{
            ml: "auto",
            bgcolor: !showHistory ? "#42A996" : "#396963",
            color: "#fff",
          }}
        >
          {showHistory ? "Hide History" : "Show History"}
        </Button>
      </Box>

      {/* Table */}
      <Box sx={{ bgcolor: "#2A3A4B", borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "rgba(30,47,64,0.95)" }}>
              <TableCell sx={{ color: "#fff" }}>Mobile</TableCell>
              <TableCell sx={{ color: "#fff" }}>Link Name</TableCell>
              <TableCell sx={{ color: "#fff" }}>
                {showHistory ? "Created On" : "Login Time"}
              </TableCell>
              {!showHistory && <TableCell />}
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredUsers.map((user, index) => (
              <TableRow key={index}>
                <TableCell sx={{ color: "#fff" }}>{user.mobile}</TableCell>

                <TableCell sx={{ color: "#fff" }}>{user.link_name}</TableCell>

                <TableCell sx={{ color: "#fff" }}>
                  {dateConverter(
                    showHistory ? user.created_at : user.login_time,
                  )}
                </TableCell>

                {!showHistory && (
                  <TableCell>
                    <Button
                      size="small"
                      sx={{ bgcolor: "#42A996", color: "#fff" }}
                      onClick={() =>
                        navigate("/createuser", {
                          state: {
                            mobile: user.mobile,
                            store_id: user.store_id,
                            mfr_id: user.mfr_id,
                          },
                        })
                      }
                    >
                      Create Customer
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

export default NewUserRequests;
